import React from 'react';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  FaCalendarAlt as FaCalendarAltRaw,
  FaClock as FaClockRaw,
  FaRocket as FaRocketRaw,
  FaSmile as FaSmileRaw,
} from 'react-icons/fa';
import { PrimaryButton } from '../../styles/marketingSharedStyles';
import {
  BackLink,
  BtnRow,
  Card,
  CharCount,
  DateTimeRow,
  EncodingRow,
  Field,
  InputWithIcon,
  Label,
  Layout,
  Main,
  MessageTextarea,
  PageSub,
  PageTitle,
  PageWrap,
  PhoneBack,
  PhoneBubble,
  PhoneHeader,
  PhoneInputLabel,
  PhoneMock,
  PhoneScreen,
  PhoneTime,
  RadioDesc,
  RadioGroup,
  RadioInput,
  RadioOption,
  Row,
  SectionHead,
  SectionTitle,
  SectionTitleRow,
  Select,
  Sidebar,
  SidebarCard,
  SidebarTitle,
  StepNum,
  TagChip,
  TagChips,
  TextInput,
  TopBar,
} from '../marketing/smsBlastPageLayout';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCalendarAlt: any = FaCalendarAltRaw;
const FaClock: any = FaClockRaw;
const FaRocket: any = FaRocketRaw;
const FaSmile: any = FaSmileRaw;

export type SmsComposerRecipientScope = 'all' | 'filtered' | 'selected' | 'single';

type SmsComposerValues = {
  messageBody: string;
  recipientScope: SmsComposerRecipientScope;
};

type ScopeCountMap = Record<SmsComposerRecipientScope, number>;

type Props = {
  open: boolean;
  variant?: 'modal' | 'page';
  /** Standalone page only: back navigation target (e.g. event setup registrants tab). */
  pageBackHref?: string;
  mode: 'single' | 'bulk';
  eventTitle: string;
  sending?: boolean;
  counts: ScopeCountMap;
  initialValues?: Partial<SmsComposerValues>;
  onClose: () => void;
  onSend: (payload: SmsComposerValues) => Promise<void>;
};

/* ---- Modal (in-page overlay) — compact composer ---- */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.48);
  backdrop-filter: blur(8px);
  display: grid;
  place-items: center;
  z-index: 1300;
  padding: 1rem;
`;

const Modal = styled.div`
  width: min(1080px, 100%);
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--surface);
  border-radius: 20px;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.14);
`;

const ModalBody = styled.div`
  padding: 1.35rem 1.2rem 1rem;
  overflow: auto;
  flex: 1;
  min-height: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.85rem;
  margin-bottom: 0.35rem;
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--onSurface);
`;

const TitleMeta = styled.p`
  margin: 0.35rem 0 0;
  color: var(--onSurfaceVariant);
  font-size: 0.88rem;
  line-height: 1.45;
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surface);
  color: var(--onSurfaceVariant);
  font-size: 1.35rem;
  line-height: 1;
  cursor: pointer;
`;

const ModalLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(280px, 1fr);
  gap: 1rem;
  align-items: start;
  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const ModalMainCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ModalSideCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ModalCard = styled.section`
  border-radius: 16px;
  padding: 1rem 1rem;
  background: var(--surface);
  border: 1px solid color-mix(in srgb, var(--outline) 78%, transparent);
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.04);
`;

const ModalCardTitle = styled.h3`
  margin: 0 0 0.65rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--onSurface);
`;

const ModalLabel = styled.label`
  display: block;
  color: var(--onSurface);
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
`;

const ModalTextArea = styled.textarea`
  box-sizing: border-box;
  width: 100%;
  min-height: 140px;
  padding: 0.58rem 0.75rem;
  border-radius: 11px;
  border: 1px solid var(--outline);
  font-size: 0.9rem;
  color: var(--onSurface);
  background: var(--surface);
  resize: vertical;
  line-height: 1.45;
`;

const ScopeSegment = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border-radius: 12px;
  border: 1px solid var(--outline);
  overflow: hidden;
  background: var(--surfaceVariant);
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ScopeButton = styled.button<{ $active: boolean }>`
  border: none;
  background: ${({ $active }) => ($active ? 'var(--surface)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--onSurface)' : 'var(--onSurfaceVariant)')};
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.5rem 0.45rem;
  cursor: pointer;
  border-right: 1px solid var(--outline);
  transition: background 0.15s ease;
  &:nth-child(3n) {
    border-right: none;
  }
  &:hover {
    background: color-mix(in srgb, var(--primary) 10%, var(--surface));
  }
`;

const TokenWrap = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  margin: 0.5rem 0 0.65rem;
`;

const TokenButton = styled.button`
  border: 1px solid color-mix(in srgb, var(--primary) 28%, var(--outline));
  background: color-mix(in srgb, var(--primary) 8%, var(--surface));
  color: var(--onSurfaceVariant);
  border-radius: 999px;
  padding: 0.22rem 0.55rem;
  font-size: 0.74rem;
  font-weight: 600;
  cursor: pointer;
`;

const Footer = styled.div<{ $spread?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $spread }) => ($spread ? 'space-between' : 'flex-end')};
  gap: 0.6rem;
  flex-wrap: wrap;
  padding: 1rem 1.2rem 1.15rem;
`;

const GhostButton = styled.button`
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 12px;
  padding: 0.52rem 0.95rem;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const LaunchButton = styled(PrimaryButton)`
  min-height: 2.5rem;
`;

const PhonePreviewBubble = styled.div`
  border-radius: 12px;
  background: var(--primaryContainer);
  padding: 0.75rem 0.9rem;
  font-size: 0.85rem;
  color: var(--onSurface);
  line-height: 1.4;
  white-space: pre-wrap;
`;

const helperTokens = [
  '{{full_name}}',
  '{{ticket_type_name}}',
  '{{quantity}}',
  '{{event_title}}',
  '{{event_start_date}}',
  '{{event_start_time}}',
  '{{event_location}}',
  '{{calendar_url}}',
  '{{calendar_ics_url}}',
];

const SMS_CHAR_LIMIT = 160;

const defaultValues: SmsComposerValues = {
  messageBody: '',
  recipientScope: 'all',
};

const renderScopeLabel = (scope: SmsComposerRecipientScope, count: number) => {
  if (scope === 'single') return `Single registrant (${count})`;
  if (scope === 'selected') return `Selected only (${count})`;
  if (scope === 'filtered') return `Filtered results (${count})`;
  return `All registrants (${count})`;
};

const EventRegistrantSmsComposer: React.FC<Props> = ({
  open,
  variant = 'modal',
  pageBackHref = '/events',
  mode,
  eventTitle,
  sending = false,
  counts,
  initialValues,
  onClose,
  onSend,
}) => {
  const [form, setForm] = React.useState<SmsComposerValues>(defaultValues);
  const [messageName, setMessageName] = React.useState('');
  const [sendNow] = React.useState(true);
  const [scheduleDate] = React.useState(() => new Date().toISOString().slice(0, 10));
  const [scheduleTime] = React.useState('09:00');

  React.useEffect(() => {
    if (!open) return;
    setForm({
      ...defaultValues,
      ...initialValues,
      recipientScope: mode === 'single' ? 'single' : initialValues?.recipientScope ?? 'filtered',
    });
    setMessageName((prev) => (prev.trim() ? prev : `SMS — ${eventTitle}`));
  }, [open, mode, initialValues, eventTitle]);

  if (!open) return null;

  const selectedScope = mode === 'single' ? 'single' : form.recipientScope;
  const recipientCount = counts[selectedScope] ?? 0;
  const sendDisabled = sending || !form.messageBody.trim() || recipientCount < 1;
  /** Full marketing layout (Create SMS Blast) when opened as a route (`variant="page"`). */
  const marketingSmsPage = variant === 'page';
  const previewText =
    form.messageBody.trim() ||
    'Hi {{first_name}}! Our Summer Weekend Sale is live. Use code SUMMER20 for 20% off site-wide!';
  const charCount = form.messageBody.length;

  const insertToken = (token: string) => {
    setForm((prev) => ({
      ...prev,
      messageBody: `${prev.messageBody}${prev.messageBody ? ' ' : ''}${token}`,
    }));
  };

  const runSend = () =>
    void onSend({
      recipientScope: selectedScope,
      messageBody: form.messageBody,
    });

  if (marketingSmsPage) {
    const audienceSummary =
      mode === 'bulk'
        ? `${renderScopeLabel(selectedScope as SmsComposerRecipientScope, recipientCount)}. Uses each registration’s phone on file (E.164 when possible).`
        : `One registrant — ${recipientCount ? 'phone on file' : 'missing phone will be skipped'}.`;

    return (
      <PageWrap>
        <TopBar>
          <BackLink to={pageBackHref}>← Back</BackLink>
          <LaunchButton type="button" onClick={runSend} disabled={sendDisabled}>
            <FaRocket size={14} /> {sending ? 'Launching…' : 'Launch Message'}
          </LaunchButton>
        </TopBar>

        <Layout>
          <Main>
            <div>
              <PageTitle>Create SMS Blast</PageTitle>
              <PageSub>Compose, segment, and schedule your message for maximum impact.</PageSub>
            </div>

            <Card>
              <SectionHead>
                <SectionTitleRow>
                  <StepNum>1</StepNum>
                  <SectionTitle>Message Details</SectionTitle>
                </SectionTitleRow>
              </SectionHead>
              <Row>
                <Field>
                  <Label>Message Name</Label>
                  <TextInput
                    type="text"
                    placeholder="e.g., Event reminder — April service"
                    value={messageName}
                    onChange={(e) => setMessageName(e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>Event</Label>
                  <TextInput type="text" value={eventTitle} disabled aria-readonly />
                </Field>
              </Row>
              {mode === 'bulk' ? (
                <Field style={{ marginTop: '1rem' }}>
                  <Label>Recipient audience</Label>
                  <Select
                    value={form.recipientScope}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        recipientScope: e.target.value as SmsComposerRecipientScope,
                      }))
                    }
                    aria-label="Recipient audience"
                  >
                    <option value="all">All registrants ({counts.all})</option>
                    <option value="filtered">Filtered results ({counts.filtered})</option>
                    <option value="selected">Selected only ({counts.selected})</option>
                  </Select>
                </Field>
              ) : null}
              <Field style={{ marginTop: '1rem' }}>
                <Label>Recipient phones (from registrations)</Label>
                <MessageTextarea
                  style={{ minHeight: '84px' }}
                  readOnly
                  value={audienceSummary}
                  aria-label="Recipient summary"
                />
                <EncodingRow style={{ justifyContent: 'space-between' }}>
                  <span>Phones are taken from each registration row.</span>
                  <CharCount>
                    Recipients:{' '}
                    <span className={recipientCount > 0 ? 'purple' : ''}>{recipientCount}</span>
                  </CharCount>
                </EncodingRow>
              </Field>
            </Card>

            <Card>
              <SectionHead>
                <SectionTitleRow>
                  <StepNum>2</StepNum>
                  <SectionTitle>Message Body</SectionTitle>
                </SectionTitleRow>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <FaSmile size={18} style={{ color: 'var(--onSurfaceVariant)' }} aria-hidden />
                  <LaunchButton type="button" disabled aria-disabled title="Templates are not available for event SMS yet">
                    Use Template
                  </LaunchButton>
                </div>
              </SectionHead>
              <TagChips>
                {helperTokens.map((tag) => (
                  <TagChip key={tag} type="button" onClick={() => insertToken(tag)}>
                    {tag}
                  </TagChip>
                ))}
              </TagChips>
              <MessageTextarea
                placeholder="Type your message here..."
                value={form.messageBody}
                onChange={(e) => setForm((prev) => ({ ...prev, messageBody: e.target.value }))}
                aria-label="Message body"
              />
              <EncodingRow>
                <span>GSM7 Encoding</span>
                <CharCount>
                  <span className={charCount > 0 ? 'purple' : ''}>{charCount}</span>/{SMS_CHAR_LIMIT}
                </CharCount>
              </EncodingRow>
            </Card>

            <Card>
              <SectionHead>
                <SectionTitleRow>
                  <StepNum>3</StepNum>
                  <SectionTitle>Delivery Schedule</SectionTitle>
                </SectionTitleRow>
              </SectionHead>
              <RadioGroup>
                <RadioOption>
                  <RadioInput type="radio" name="event-sms-schedule" checked={sendNow} readOnly />
                  <div>
                    Send Immediately
                    <RadioDesc>Message will send as soon as you launch.</RadioDesc>
                  </div>
                </RadioOption>
                <RadioOption style={{ opacity: 0.45, pointerEvents: 'none' }}>
                  <RadioInput type="radio" name="event-sms-schedule" checked={false} readOnly disabled />
                  <div>
                    Schedule for Later
                    <RadioDesc>Not available for event SMS yet.</RadioDesc>
                  </div>
                </RadioOption>
              </RadioGroup>
              <DateTimeRow>
                <Field>
                  <Label>Date</Label>
                  <InputWithIcon>
                    <FaCalendarAlt size={14} />
                    <input type="date" value={scheduleDate} disabled aria-label="Schedule date" />
                  </InputWithIcon>
                </Field>
                <Field>
                  <Label>Time (local)</Label>
                  <InputWithIcon>
                    <FaClock size={14} />
                    <input type="time" value={scheduleTime} disabled aria-label="Schedule time" />
                  </InputWithIcon>
                </Field>
              </DateTimeRow>
            </Card>
          </Main>

          <Sidebar>
            <SidebarCard>
              <SidebarTitle>Mobile Preview</SidebarTitle>
              <PhoneMock>
                <PhoneScreen>
                  <PhoneHeader>
                    <PhoneBack>←</PhoneBack>
                    <span>SMS Blast</span>
                    <span style={{ marginLeft: 'auto' }}>ℹ</span>
                  </PhoneHeader>
                  <PhoneTime>TODAY {new Date().toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</PhoneTime>
                  <PhoneBubble>{previewText}</PhoneBubble>
                  <PhoneInputLabel>Text Message</PhoneInputLabel>
                </PhoneScreen>
              </PhoneMock>
              <BtnRow style={{ marginTop: '1rem' }}>
                <LaunchButton type="button" onClick={runSend} disabled={sendDisabled}>
                  <FaRocket size={14} /> Launch Message
                </LaunchButton>
              </BtnRow>
            </SidebarCard>
          </Sidebar>
        </Layout>
      </PageWrap>
    );
  }

  const composer = (
    <Modal onClick={(event) => event.stopPropagation()}>
      <ModalBody>
        <HeaderRow>
          <TitleBlock>
            <Title>{mode === 'single' ? 'Send SMS to registrant' : 'Send bulk SMS'}</Title>
            <TitleMeta>{eventTitle}</TitleMeta>
          </TitleBlock>
          <CloseButton type="button" onClick={onClose} aria-label="Close SMS composer">
            ×
          </CloseButton>
        </HeaderRow>

        <ModalLayout>
          <ModalMainCol>
            <ModalCard>
              <ModalCardTitle>Message Details</ModalCardTitle>
              {mode === 'bulk' ? (
                <>
                  <ModalLabel>Recipients</ModalLabel>
                  <ScopeSegment>
                    {(['all', 'filtered', 'selected'] as SmsComposerRecipientScope[]).map((scope) => (
                      <ScopeButton
                        key={scope}
                        type="button"
                        $active={selectedScope === scope}
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            recipientScope: scope,
                          }))
                        }
                      >
                        {renderScopeLabel(scope, counts[scope])}
                      </ScopeButton>
                    ))}
                  </ScopeSegment>
                </>
              ) : (
                <EncodingRow style={{ justifyContent: 'space-between', marginTop: 0 }}>
                  <span>Mode</span>
                  <span>{renderScopeLabel('single', recipientCount)}</span>
                </EncodingRow>
              )}
            </ModalCard>

            <ModalCard>
              <ModalCardTitle>Message Body</ModalCardTitle>
              <ModalLabel>Insert personalization token</ModalLabel>
              <TokenWrap>
                {helperTokens.map((token) => (
                  <TokenButton key={token} type="button" onClick={() => insertToken(token)}>
                    {token}
                  </TokenButton>
                ))}
              </TokenWrap>
              <ModalTextArea
                value={form.messageBody}
                placeholder="Type your event SMS message here..."
                onChange={(event) => setForm((prev) => ({ ...prev, messageBody: event.target.value }))}
              />
              <EncodingRow>
                <span>Recipients: {recipientCount}</span>
                <span>
                  Characters: {form.messageBody.length}/{SMS_CHAR_LIMIT}
                </span>
              </EncodingRow>
            </ModalCard>
          </ModalMainCol>

          <ModalSideCol>
            <ModalCard>
              <ModalCardTitle>Mobile Preview</ModalCardTitle>
              <PhonePreviewBubble>{previewText}</PhonePreviewBubble>
            </ModalCard>
          </ModalSideCol>
        </ModalLayout>
      </ModalBody>
      <Footer $spread>
        <GhostButton type="button" onClick={onClose}>
          Cancel
        </GhostButton>
        <LaunchButton type="button" disabled={sendDisabled} onClick={runSend}>
          <FaRocket size={14} /> {sending ? 'Sending…' : mode === 'single' ? 'Send SMS' : `Send (${recipientCount})`}
        </LaunchButton>
      </Footer>
    </Modal>
  );

  return <Overlay onClick={onClose}>{composer}</Overlay>;
};

export default EventRegistrantSmsComposer;
